import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Activity } from "lucide-react";

const STATE_STYLES = {
  NOT_STARTED: "bg-gray-100 text-gray-700",
  PENDING: "bg-amber-100 text-amber-900",
  RUNNING: "bg-blue-100 text-blue-900 animate-pulse",
  COMPLETED: "bg-emerald-100 text-emerald-900",
  FAILED: "bg-red-100 text-red-900",
  BLOCKED: "bg-red-200 text-red-950",
};

export const SegmentedRunProgress = ({ jobId, status, onStatus }) => {
  const [d, setD] = useState(status || null);
  const timer = useRef(null);

  useEffect(() => { if (status) setD(status); }, [status]);

  useEffect(() => {
    if (!jobId) return undefined;
    const poll = async () => {
      try {
        const { data } = await api.get(`/reader/jobs/${jobId}/segmented-run/status`);
        setD(data);
        onStatus?.(data);
      } catch { /* pannello read-only: nessuna azione */ }
    };
    poll();
    timer.current = setInterval(poll, 5000);
    return () => clearInterval(timer.current);
  }, [jobId]);

  if (!d?.authorization_present) return null;

  return (
    <div className="border border-border p-2 space-y-2" data-testid="segmented-run-progress-panel">
      <div className="font-bold flex items-center gap-1.5">
        <Activity size={13} />Pannello avanzamento run segmentato — <span className="font-mono text-[10px]">{d.logical_reader_run_id}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 text-[11px]" data-testid="segmented-run-progress-totals">
        <div>Stato logical run: <b data-testid="segmented-run-status">{d.run_status}</b></div>
        <div>Segmento corrente: <b data-testid="segmented-run-current">{d.current_segment || "nessuno"}</b></div>
        <div>Costo reale cumulativo: <b data-testid="segmented-run-cumulative-cost">${d.real_cost_cumulative_usd}</b></div>
        <div>Budget residuo: <b>${d.budget_residual_usd}</b></div>
        <div>Riserva worst-case rimanente: <b>${d.worst_case_reserve_remaining_usd}</b></div>
        <div>Retry avvenuti: <b data-testid="segmented-run-no-retry">{d.no_retry_confirmed ? "NESSUNO (confermato)" : "ANOMALIA"}</b></div>
      </div>
      <div className="table-scroll">
      <table className="w-full text-[10px]" data-testid="segmented-run-segments-table">
        <thead><tr className="border-b border-border bg-muted text-left">
          {["Seg", "Stato", "Img", "Tok in", "Tok out", "Costo reale", "Durata", "response_id", "output hash", "Errore"].map((h) =>
            <th key={h} className="px-1.5 py-1 font-medium whitespace-nowrap">{h}</th>)}
        </tr></thead>
        <tbody>
          {(d.segments || []).map((s) => (
            <tr key={s.segment_id} className="border-b border-border" data-testid={`segmented-run-row-${s.segment_id}`}>
              <td className="px-1.5 py-1 font-bold whitespace-nowrap">{s.segment_id}<span className="font-normal text-muted-foreground"> {s.component_code || ""}</span></td>
              <td className="px-1.5 py-1"><span className={`px-1 py-0.5 font-bold ${STATE_STYLES[s.state] || ""}`}>{s.state}</span></td>
              <td className="px-1.5 py-1">{s.n_images}</td>
              <td className="px-1.5 py-1">{s.input_tokens ?? "—"}</td>
              <td className="px-1.5 py-1">{s.output_tokens ?? "—"}</td>
              <td className="px-1.5 py-1">{s.real_cost_usd != null ? `$${s.real_cost_usd}` : "—"}</td>
              <td className="px-1.5 py-1">{s.duration_s != null ? `${s.duration_s}s` : "—"}</td>
              <td className="px-1.5 py-1 font-mono">{s.response_id_short || "—"}</td>
              <td className="px-1.5 py-1 font-mono">{s.output_hash_short || "—"}</td>
              <td className="px-1.5 py-1 text-red-800 break-anywhere">{s.error || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="text-[10px] text-muted-foreground">
        Vista read-only (solo polling GET): da questo pannello non è possibile reinviare segmenti. Esecuzione sequenziale, nessun retry automatico, esito finale obbligatorio {d.final_state_forced}.
      </div>
    </div>
  );
};
